import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';

import { setStudentsList } from './studentsSlice';

import {
  Student,
  StudentResponse,
  StudentType,
  Person,
  StudentAvatar,
  EntryTypesResponse,
  StudentDocuments,
  TypeDocumentStudent,
  StudentDocumentPayload,
  YearsStudentPeriod,
  EntryType,
} from '../interfaces/students.interface';
import { PdfCertificate, StudentRecords } from '../interfaces/studentRecords.interface';
import { AccessState } from '../../../../types';
import { Mention } from '../interfaces/mentions.interfaces';
import { StudentToEdit } from '../interfaces/student-edit.interfaces';
import { setStudentDataEdit } from '../slices/studentsSlice';
import {
  EmergencyContact,
  EmergencyContactUpdatePayload,
  PersonRelativePayload,
  StudentRecordAcademic,
  UpdateRelativesPersonExisting,
} from '../interfaces/studentRecordAcademic.interfaces';
import {
  StudentJobInformationPayload,
  UpdateStudentRecordAcademicPayload,
} from '../interfaces/editStudentRecordAcademic.interfaces';
import { StudentStore } from '../interfaces/studentsStore.interface';
import { EducationPayload } from '../interfaces/educationStudents.interfaces';
import { SocialNetworkByPerson } from '../interfaces/socialNetworkByPerson';

export const studentsTags = middlewareApi.enhanceEndpoints({
  addTagTypes: [
    'Students',
    'Persons',
    'StudentsEmergencyContact',
    'StudentsAvailables',
    'StudentDocuments',
  ],
});
// Define a service using a base URL and expected endpoints
export const studentsApi = studentsTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all students
     */
    getStudents: builder.query<
      StudentResponse,
      {
        profileId?: number;
        page: number;
        perPage: number;
        search: string;
        offerId?: number;
        isPaid?: boolean;
        sort?: string;
        type_sort?: string;
        instituteId?: number;
        typeStudentId?: number;
        entryTypeId?: number;
        statusId?: number;
        educationLevelId?: number;
        type_institute?: number;
        date_to?: string;
        date_from?: string;
      }
    >({
      queryFn: async (
        {
          profileId,
          page,
          search,
          perPage,
          offerId,
          isPaid,
          sort,
          type_sort,
          instituteId,
          typeStudentId,
          entryTypeId,
          statusId,
          educationLevelId,
          type_institute,
          date_to,
          date_from,
        },
        { dispatch, getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;
          const { data } = await mainApi.get<StudentResponse>('/students', {
            params: {
              user_profile_id,
              page,
              size: perPage,
              // sort: 'students.id',
              type_sort: type_sort || 'desc',
              ...(sort ? { sort } : { sort: 'id' }),
              ...((offerId || 0) > 0 && { offer_id: offerId }),

              is_paid: isPaid,
              search,
              ...(instituteId && { institute_id: instituteId }),
              ...(typeStudentId && { type_student_id: typeStudentId }),
              ...(entryTypeId && { type_entry_id: entryTypeId }),
              ...(statusId && { status_id: statusId }),
              ...(educationLevelId && { education_levels_id: educationLevelId }),
              ...(type_institute && { type_institute }),
              ...(date_to && { date_to }),
              ...(date_from && { date_from }),
            },
          });

          dispatch(setStudentsList(data.data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Students', id } as const)),
              { type: 'Students', id: 'LIST' },
            ]
          : [{ type: 'Students', id: 'LIST' }],
    }),

    /**
     * @GET all students Avalibles
     */
    getAvaliblesStudents: builder.query<Student[], { profileId: Number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get<Student[]>('/students', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'StudentsAvailables', id: 'COMBO_STNDTS_LIST' }],
    }),

    /**
     * @GET student by id
     */
    getStudent: builder.query<Student, { profileId?: number; studentId: number }>({
      queryFn: async ({ profileId, studentId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<Student>(`/students/${studentId}`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'Students', profileId }],
    }),

    /**
     * @POST create student
     */
    addStudent: builder.mutation<Student, { profileId?: number; studentPayload: Partial<any> }>({
      queryFn: async ({ profileId, studentPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.post<Student>('/students', {
            user_profile_id,
            ...studentPayload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Students', id: 'LIST' }],
    }),

    /**
     * @POST create student
     */
    addStudentExternal: builder.mutation<
      Student,
      { profileId?: number; studentPayload: Partial<any> }
    >({
      queryFn: async ({ profileId, studentPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.post<Student>('/students/external', {
            user_profile_id,
            ...studentPayload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Students', id: 'LIST' }],
    }),

    /**
     * @POST create student store
     */
    addStudentStore: builder.mutation<
      StudentStore,
      { profileId?: number; studentPayload: StudentStore }
    >({
      queryFn: async ({ profileId, studentPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.post<StudentStore>('/students', {
            user_profile_id,
            ...studentPayload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Students', id: 'LIST' }],
    }),

    /**
     * @POST add student education
     */
    addEducationStudent: builder.mutation<
      EducationPayload,
      { profileId?: number; educationPayload: EducationPayload; studentId: string }
    >({
      queryFn: async ({ profileId, educationPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.post<EducationPayload>('/students/person-education', {
            user_profile_id,
            ...educationPayload,
          });
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { studentId }) => [{ type: 'Students', studentId }],
    }),

    /**
     * @POST add Avatar student
     */
    addAvatarStudent: builder.mutation<
      Student,
      { avatarStudentPayload: StudentAvatar; studentId: number }
    >({
      queryFn: async ({ avatarStudentPayload, studentId }) => {
        try {
          const form = new FormData();
          form.append('files[]', avatarStudentPayload.files);
          form.append('type_document', '6');
          form.append('period', '1');
          form.append('user_profile_id', String(avatarStudentPayload.user_profile_id));

          const { data } = await mainApi.post<Student>(`/students/${studentId}/photo`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Students', id: 'LIST' }],
    }),

    saveAvatarStudent: builder.mutation<any, { file: File; profileId?: number }>({
      queryFn: async ({ file, profileId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const form = new FormData();
          form.append('files[]', file);
          form.append('type_document', '7');
          form.append('user_profile_id', user_profile_id + '');

          const { data } = await mainApi.post<string>('/students/photo', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Students', id: 'LIST' }],
    }),

    /**
     * @PUT update student
     */
    updateStudent: builder.mutation<
      StudentStore,
      { profileId?: number; studentId: string; studentPayload: Partial<StudentStore> }
    >({
      queryFn: async ({ profileId, studentId, studentPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.put<StudentStore>(
            `/students/${studentId}/academic-record`,
            {
              user_profile_id,
              ...studentPayload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { studentId }) => [
        { type: 'Students', id: studentId },
        { type: 'Students', studentId },
      ],
    }),

    /**
     * @DELETE remove student
     */
    deleteStudent: builder.mutation<Student, { profileId: number; studentId: number }>({
      queryFn: async ({ profileId, studentId }) => {
        try {
          const { data } = await mainApi.delete(`students/${studentId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Students', id: 'LIST' }],
    }),

    /**
     * @DELETE remove educaton student
     */
    deleteEducationStudent: builder.mutation<
      EducationPayload,
      { profileId: number; studentId: number; educationId: number }
    >({
      queryFn: async ({ profileId, studentId, educationId }) => {
        try {
          const { data } = await mainApi.delete(`students/personEducation/${educationId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { studentId }) => [{ type: 'Students', studentId }],
    }),

    /**
     * @GET Households id 1
     */
    // vivientas:          VV
    // tipo de vivienta:   TV
    // servicios basicos   SB
    // Generos             G
    // estado civil        SM
    // religion            TR
    // ciudad              C
    // sector              S
    // etnia               ET
    // tipo id             TI
    // modalidad           M
    // jornada             J
    // parentesco          P
    // idiomas             I

    /**
     * @GET student Types
     */
    getStudentsTypes: builder.query<StudentType[], { profileId?: number }>({
      queryFn: async ({ profileId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<StudentType[]>(`/type-students`, {
            params: {
              user_profile_id,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'Students', profileId }],
    }),

    /**
     * @GET entry types all
     */
    getEntryTypesAll: builder.query<EntryType[], { profileId?: number }>({
      queryFn: async ({ profileId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<EntryType[]>(`/entry-types`, {
            params: {
              user_profile_id,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'Students', profileId }],
    }),

    /**
     * @GET student record academic
     */
    getStudentRecordAcademic: builder.query<StudentRecordAcademic, { studentId: string }>({
      queryFn: async ({ studentId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<StudentRecordAcademic>(
            `/students/${studentId}/academic-record`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { studentId }) => [{ type: 'Students', studentId }],
    }),

    /**
     * @PUT update student new
     */
    updateStudentRecordAcademic: builder.mutation<
      UpdateStudentRecordAcademicPayload,
      { studentId: string; studentPayload: Partial<UpdateStudentRecordAcademicPayload> }
    >({
      queryFn: async ({ studentId, studentPayload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.put<UpdateStudentRecordAcademicPayload>(
            `students/${studentId}/academic-record`,
            {
              user_profile_id,
              ...studentPayload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { studentId }) => [
        { type: 'Students', id: 'LIST' },
        { type: 'Students', studentId },
      ],
    }),

    /**
     * @GET person by id
     * @Permission persons-actualizar-person
     */
    getPerson: builder.query<StudentToEdit, { profileId?: number; personId: number }>({
      queryFn: async ({ profileId, personId }, { dispatch, getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<StudentToEdit>(`/persons/${personId}`, {
            params: {
              user_profile_id,
            },
          });

          dispatch(setStudentDataEdit(data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [
        { type: 'Persons', profileId, id: 'PERSON_ID' },
      ],
    }),

    /**
     * @GET obtain mentions
     */
    getMentions: builder.query<Mention[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get<Mention[]>(`/mention`, {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @PUT update person
     */
    updatePerson: builder.mutation<
      Person,
      { profileId: number; personId: number; personPayload: Partial<Person> }
    >({
      queryFn: async ({ profileId, personId, personPayload }) => {
        try {
          const { data } = await mainApi.put<Person>(`/persons/${personId}`, {
            user_profile_id: profileId,
            ...personPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Persons', id: 'PERSON_ID' }],
    }),

    /**
     * @GET obtener estudentRecords por estudiante
     */
    getStudentRecordsByStudent: builder.query<StudentRecords[], { studentId?: string }>({
      queryFn: async ({ studentId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const { data: dataFixed } = await mainApi.get<StudentRecords[]>(
            `/students/${studentId}/student-records`,
            {
              params: {
                user_profile_id: currentProfile?.profileId,
                data: 'all',
              },
            }
          );
          const data = Array.isArray(dataFixed) ? dataFixed : [];
          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET student by id
     */
    getStudentRegisterCertificate: builder.query<
      PdfCertificate,
      { student_record_id: number; user_id: number; period_id: number }
    >({
      queryFn: async ({ student_record_id, user_id, period_id }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<PdfCertificate>(
            `/certificates/student-register/preview`,
            {
              params: { user_profile_id, student_record_id, user_id, period_id },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { user_id }) => [{ type: 'Students', user_id }],
    }),

    /**
     * @GET get entry types
     * @Params offerKey: offecr description as key
     */
    getEntryTypes: builder.query<EntryTypesResponse, { offerKey?: string }>({
      queryFn: async ({ offerKey }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<EntryTypesResponse>(`/students/entry/types`, {
            params: { user_profile_id, ...(offerKey && { ent_ty_description: offerKey }) },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    setEarlyPayment: builder.mutation<any, { studentId: number; applyEarlyPayment: boolean }>({
      queryFn: async ({ studentId, applyEarlyPayment }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.put<any>(`students/${studentId}/early-payment`, {
            user_profile_id,
            stud_apply_early_payment: applyEarlyPayment,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Students', id: 'LIST' }],
    }),

    /**
     * @GET get documents by student
     * @Params studentId: studentId description as key
     */
    getStudentDocuments: builder.query<StudentDocuments[], { studentId?: string }>({
      queryFn: async ({ studentId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<StudentDocuments[]>(
            `/students/${studentId}/documents`,
            {
              params: {
                user_profile_id /*  ...(studentId && { ent_ty_description: studentId }) */,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'StudentDocuments', id: 'LIST' }],
    }),

    /**
     * @GET get types documents by student
     * @Params studentId: studentId description as key
     */
    getTypeDocumentsByStudent: builder.query<TypeDocumentStudent[], { studentId?: string }>({
      queryFn: async ({ studentId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<TypeDocumentStudent[]>(
            `/students/${studentId}/type-documents`,
            {
              params: {
                user_profile_id /*  ...(studentId && { ent_ty_description: studentId }) */,
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'StudentDocuments', id: 'LIST' }],
    }),

    /**
     * Create document student
     */
    saveStudentDocument: builder.mutation<
      any,
      { studentId: string; payload: StudentDocumentPayload }
    >({
      queryFn: async ({ studentId, payload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<any>(`students/${studentId}/documents`, {
            user_profile_id,
            ...payload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'StudentDocuments', id: 'LIST' }],
    }),

    /**
     * Update document student
     */
    updateStudentDocument: builder.mutation<
      any,
      { studentDocumentId: number; payload: StudentDocumentPayload }
    >({
      queryFn: async ({ studentDocumentId, payload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.put<any>(`students/${studentDocumentId}/documents`, {
            user_profile_id,
            ...payload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'StudentDocuments', id: 'LIST' }],
    }),

    /**
     * delete document student
     */
    deleteStudentDocument: builder.mutation<any, { studentDocumentId: number }>({
      queryFn: async ({ studentDocumentId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.delete<any>(`students/${studentDocumentId}/documents`, {
            data: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'StudentDocuments', id: 'LIST' }],
    }),

    getStudentCertificate: builder.query<
      any,
      {
        user_profile_id: number;
        student_record_id: number;
        lang: string;
        user_id: number;
        app_acronym: string;
        period_id?: number;
        to?: string;
        consul?: string;
        practices?: {
          isCERPPP: boolean;
          name: string;
          title: string;
          position: string;
          enterprise: string;
          city: string;
        };
      }
    >({
      queryFn: async ({
        user_profile_id,
        student_record_id,
        lang,
        user_id,
        app_acronym,
        period_id,
        to,
        consul,
        practices,
      }) => {
        try {
          const { data } = await mainApi.get<any>(`/certificates/preview`, {
            params: {
              user_profile_id,
              student_record_id,
              user_id,
              lang,
              app_acronym,
              ...(period_id && { period_id }),
              ...(to && { to }),
              ...(consul && { consul }),
              ...(practices && {
                practices: [
                  practices.title,
                  practices.name,
                  practices.position,
                  practices.enterprise,
                  practices.city,
                ],
              }),
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET get types documents by student
     * @Params studentId: studentId description as key
     */
    getStudentYearsPeriods: builder.query<YearsStudentPeriod[], { studentId?: string }>({
      queryFn: async ({ studentId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<YearsStudentPeriod[]>(
            `/students/${studentId}/periods`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      // providesTags: [{ type: 'StudentDocuments', id: 'LIST' }],
    }),

    /**
     * @PUT update student couhnselor
     */
    updateStudentCounselor: builder.mutation<
      Student,
      { collaborator_id?: number; stdudent_record_id: number; profileId?: number }
    >({
      queryFn: async ({ profileId, collaborator_id, stdudent_record_id }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.put<Student>(`/students/academic-advisor`, {
            user_profile_id,
            collaborator_id,
            stdudent_record_id,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Students', id: 'LIST' }],
    }),
    //

    addStudentJobInformation: builder.mutation<
      Student,
      {
        payload: StudentJobInformationPayload;
        studentId: string;
      }
    >({
      queryFn: async ({ payload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<Student>(`/students/person-jobs`, {
            user_profile_id,
            personJobs: [{ ...payload }],
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { studentId }) => [{ type: 'Students', studentId }],
    }),
    editStudentJobInformation: builder.mutation<
      Student,
      {
        payload: StudentJobInformationPayload;
        studentId: string;
        jobId: number;
      }
    >({
      queryFn: async ({ payload, jobId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.put<Student>(`/students/person-jobs`, {
            user_profile_id,
            personJobs: [{ ...payload, id: jobId }],
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { studentId }) => [{ type: 'Students', studentId }],
    }),

    deleteStudentJobInformation: builder.mutation<
      Student,
      {
        jobId: number;
        studentId: string;
      }
    >({
      queryFn: async ({ jobId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.delete<Student>(`/students/personJob/${jobId}`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { studentId }) => [{ type: 'Students', studentId }],
    }),

    /**
     * @POST Create emergency contact
     */
    addStudentEmergencyContact: builder.mutation<
      Student,
      {
        payload: Omit<EmergencyContact, 'id' | 'cat_name'>[];
        studentPersonId: string;
        studentId: string;
      }
    >({
      queryFn: async ({ payload, studentPersonId, studentId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<Student>(`/emergency-contact`, {
            user_profile_id,
            person_id: studentPersonId,
            emergencyContacts: [...payload],
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { studentId }) => [
        { type: 'StudentsEmergencyContact', studentId },
      ],
    }),
    // Fin new emergency contact

    /**
     * @PUT Update emergency contact
     */
    updateStudentEmergencyContact: builder.mutation<
      Student,
      {
        payload: EmergencyContactUpdatePayload;
        emergencyContactId: number;
        studentId: string;
      }
    >({
      queryFn: async ({ payload, emergencyContactId, studentId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<Student>(`/emergency-contact/${emergencyContactId}`, {
            user_profile_id,
            ...payload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { studentId }) => [
        { type: 'StudentsEmergencyContact', studentId },
      ],
    }),
    // Fin new emergency contact

    /**
     * @DELETE delelte emergency contact
     */
    deleteStudentEmergencyContact: builder.mutation<
      Student,
      {
        emergencyContactId: number;
        studentId: string;
      }
    >({
      queryFn: async ({ emergencyContactId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.delete<Student>(
            `/emergency-contact/${emergencyContactId}`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { studentId }) => [
        { type: 'StudentsEmergencyContact', studentId },
      ],
    }),
    // Fin delete emergency contact

    /**
     * @GET get EMERGENCY CONTACT BY STUDENT
     */
    getEmergencyContactsByPerson: builder.query<
      EmergencyContact[],
      { studentId?: string; personId?: string }
    >({
      queryFn: async ({ personId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<EmergencyContact[]>(
            `person/${personId}/emergency-contact`,
            {
              params: {
                user_profile_id /*  ...(studentId && { ent_ty_description: studentId }) */,
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { studentId }) => [
        { type: 'StudentsEmergencyContact', studentId },
      ],
    }),

    /**
     * @POST Create person (new or existent) relative
     */
    addStudentRelativePerson: builder.mutation<
      Student,
      {
        payload: PersonRelativePayload[];
        studentPersonId: string;
        studentId: string;
      }
    >({
      queryFn: async ({ payload, studentPersonId, studentId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<Student>(`/students/person-relative`, {
            user_profile_id,
            person_id: studentPersonId,
            personRelatives: [...payload],
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      // se invalida la data del estudiante porque el get de los familiares no tiene get independiente aun
      invalidatesTags: (result, error, { studentId }) => [{ type: 'Students', studentId }],
    }),
    // Fin new relative contact

    /**
     * @PUT Update person (existent) relative
     */
    updateStudentRelativePerson: builder.mutation<
      Student,
      {
        payload: UpdateRelativesPersonExisting;
        // uso solamnete para el tag de invalidacion de la peticion principal
        studentId: number;
      }
    >({
      queryFn: async ({ payload, studentId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.put<Student>(`/students/person-relative`, {
            user_profile_id,
            updatePersonRelatives: [{ ...payload }],
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      // se invalida la data del estudiante porque el get de los familiares no tiene get independiente aun
      invalidatesTags: (result, error, { studentId }) => [{ type: 'Students', studentId }],
    }),
    // Fin update relative contact

    /**
     * @DELETE delelte relative from student
     */
    deleteStudentRelative: builder.mutation<
      Student,
      {
        relativeId: number;
        studentId: string;
      }
    >({
      queryFn: async ({ relativeId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.delete<Student>(
            `/students/person-relative/relative/${relativeId}`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { studentId }) => [{ type: 'Students', studentId }],
    }),
    // Fin delete emergency contact

    //

    getPdfByStudentId: builder.query<any, { studentId: string }>({
      queryFn: async ({ studentId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const { data } = await mainApi.get<any>(`/students/${studentId}/academic-record/pdf`, {
            params: {
              user_profile_id: currentProfile?.profileId,
            },
            responseType: 'arraybuffer',
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    getSocialNetworksByPerson: builder.query<SocialNetworkByPerson[], { personId?: string }>({
      queryFn: async ({ personId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;
          const { data } = await mainApi.get<SocialNetworkByPerson[]>(
            `/persons/${personId}/social-network`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetStudentsQuery,
  useGetStudentQuery,
  useLazyGetStudentsQuery,
  useLazyGetStudentQuery,
  useAddStudentMutation,
  useAddStudentExternalMutation,
  useDeleteStudentMutation,
  useUpdateStudentMutation,
  useGetStudentsTypesQuery,
  useGetPersonQuery,
  useSaveAvatarStudentMutation,
  useGetMentionsQuery,
  useUpdatePersonMutation,
  useGetAvaliblesStudentsQuery,
  useAddAvatarStudentMutation,
  useGetStudentRecordsByStudentQuery,
  useGetStudentRegisterCertificateQuery,
  useGetEntryTypesQuery,
  useSetEarlyPaymentMutation,
  useGetStudentCertificateQuery,
  useLazyGetStudentCertificateQuery,
  useGetStudentDocumentsQuery,
  useGetTypeDocumentsByStudentQuery,
  useSaveStudentDocumentMutation,
  useUpdateStudentDocumentMutation,
  useDeleteStudentDocumentMutation,
  useGetStudentYearsPeriodsQuery,
  useGetStudentRecordAcademicQuery,
  useUpdateStudentRecordAcademicMutation,
  useUpdateStudentCounselorMutation,
  useAddStudentStoreMutation,
  useGetEntryTypesAllQuery,
  useAddEducationStudentMutation,
  useDeleteEducationStudentMutation,

  useAddStudentJobInformationMutation,
  useEditStudentJobInformationMutation,
  useDeleteStudentJobInformationMutation,
  useGetEmergencyContactsByPersonQuery,

  useAddStudentEmergencyContactMutation,
  useDeleteStudentEmergencyContactMutation,
  useAddStudentRelativePersonMutation,
  useUpdateStudentRelativePersonMutation,
  useDeleteStudentRelativeMutation,
  useUpdateStudentEmergencyContactMutation,
  useLazyGetPdfByStudentIdQuery,
  useLazyGetSocialNetworksByPersonQuery,
} = studentsApi;
